import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type Assessment from '@app/src/types/assessment'
import { SortOrder } from '@app/src/types/filter'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { useMemo } from 'react'
import { computeTemplateScores, pickLatestPerTemplate, type TemplateScores } from './profileLogic'

interface UseProviderAssessmentsResult {
  scores: Map<number, TemplateScores>
  baseline?: Assessment
  isLoading: boolean
}

const ASSESSMENT_PAGE_SIZE = 200

export function useProviderAssessments(providerId: number): UseProviderAssessmentsResult {
  const { items = [], isLoading } = useFetchCollectionWithPost<Assessment>({
    key: [FetchKey.AssessmentCollection, 'profile', providerId],
    endpoint: endpoints.assessmentCollection,
    payload: {
      sort: { order: SortOrder.DESCENDING, target: 'periodName' },
      filter: [{ name: 'providerId', filters: [{ operator: Operators.EqualTo, value: providerId }] }],
      include: ['assessmentTemplate', 'assessmentTemplate.sections'],
      pagination: { pageNumber: 1, itemsPerPage: ASSESSMENT_PAGE_SIZE },
    },
    options: { enabled: Boolean(providerId) },
  })

  const latest = useMemo(() => pickLatestPerTemplate(items), [items])

  const scores = useMemo(
    () => new Map(latest.map(assessment => [assessment.assessmentTemplateId, computeTemplateScores(assessment)])),
    [latest],
  )

  const baseline = useMemo(
    () =>
      latest.find(assessment => assessment.assessmentTemplate?.assessmentType === AssessmentType.BaselineAssessment),
    [latest],
  )

  return { scores, baseline, isLoading }
}
